import type { ContextData } from '@wirechunk/schemas/context-data/context-data.d.ts';
import type { DesignerComponent, ComponentStyleRules } from '../designer/component.d.ts';
import type {
  ConfirmationAction,
  LessonCompletionMode,
  DesignerObjectType,
} from '../graphql-api-enums.generated.ts';
import type { RemapPropertiesToUnknown } from '../util-types.ts';

// The path (slug) for visual preview (except for pages, which are previewed on their actual path).
// Paths starting with an underscore are reserved for internal use.
export const visualPreviewPath = '__visual-preview';

// The name of the cookie that's set on page load when the site is being previewed.
export const visualPreviewObjectTypeCookieName = 'wirechunkPreviewObjectType';

// The name of the URL query parameter that contains the object type to preview.
export const visualPreviewObjectTypeParamName = visualPreviewObjectTypeCookieName;

// The name of the cookie that's set on page load when the site is being previewed for free.
// This is used as a fallback to the URL query parameter so that the user can navigate around
// the site with hard navigation without the subscription error message coming up.
export const freeSitePreviewCookieName = 'wirechunkFreeSitePreview';

// The name of the URL query parameter that flags to enable viewing a site without an error message about a missing active subscription.
export const freeSitePreviewParamName = freeSitePreviewCookieName;

// The name of the URL query parameter that contains the page preview token.
export const pagePreviewTokenParamName = 'wirechunkPagePreviewToken';

// The name of the URL query parameter that contains an impersonation auth token for previews.
export const wirechunkImpersonateAuthTokenParamName = 'wirechunkImpersonateAuthToken';

// The name of the URL query parameter that contains a short-lived auth token for internal preview embeds.
export const wirechunkInternalPreviewAuthTokenParamName = 'wirechunkInternalPreviewAuthToken';

// The name of the URL query parameter that clears impersonation cookies for previews.
export const wirechunkImpersonateSignOutParamName = 'wirechunkImpersonateSignOut';

// Sent in objects from the preview iframe to the parent.
export const previewIFrameReadyMessageType = 'preview-ready';

// Sent in objects from the preview iframe to the parent with the current user id.
export const previewIFrameCurrentUserMessageType = 'preview-current-user';

// Sent in objects from the parent to the preview iframe.
export const previewIFrameParentUpdateMessageType = 'parent-update';

// Sent from the preview iframe to the parent when navigation occurs.
export const previewIFrameNavigationChangeMessageType = 'preview-navigation-change';

// Sent from the preview iframe to the parent when the user navigates back to the original page.
export const previewIFrameNavigateBackMessageType = 'preview-navigate-back';

// Sent from the preview iframe to the parent when the user scrolls.
export const previewIFrameScrollMessageType = 'preview-scroll';

// Sent from the preview iframe to the parent.
export type PreviewIFrameReadyMessage = {
  type: typeof previewIFrameReadyMessageType;
};

export type PreviewIFrameCurrentUserMessage = {
  type: typeof previewIFrameCurrentUserMessageType;
  userId: string | null;
};

export const isPreviewIFrameCurrentUserMessage = (
  data: unknown,
): data is PreviewIFrameCurrentUserMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as RemapPropertiesToUnknown<PreviewIFrameCurrentUserMessage>).type ===
    previewIFrameCurrentUserMessageType;

// Sent from the parent to the preview iframe.
export type PreviewIFrameObjectDataMessage = {
  type: typeof previewIFrameParentUpdateMessageType;
  object: DesignerPreviewObjectWithComponents;
};

// Sent from the preview iframe to the parent when navigation occurs.
export type PreviewIFrameNavigationChangeMessage = {
  type: typeof previewIFrameNavigationChangeMessageType;
};

export type PreviewIFrameNavigateBackMessage = {
  type: typeof previewIFrameNavigateBackMessageType;
};

export type PreviewIFrameScrollMessage = {
  type: typeof previewIFrameScrollMessageType;
};

// Designer selection mode (parent -> iframe)
export const previewIFrameSetDesignerModeMessageType = 'parent-set-designer-mode';
export type PreviewIFrameSetDesignerModeMessage = {
  type: typeof previewIFrameSetDesignerModeMessageType;
  mode: 'off' | 'select';
  broadcastRects: boolean;
};

// Component selected in iframe (iframe -> parent)
export const previewIFrameComponentSelectedMessageType = 'preview-component-selected';
export type PreviewIFrameComponentSelectedMessage = {
  type: typeof previewIFrameComponentSelectedMessageType;
  componentId: string;
  rect: { left: number; top: number; width: number; height: number };
  clickPosition: { x: number; y: number };
  // 'click' when user-initiated via pointer, 'update' for programmatic updates (scroll/resize/parent-set)
  origin: 'click' | 'update';
};

export const isPreviewIFrameComponentSelectedMessage = (
  data: unknown,
): data is PreviewIFrameComponentSelectedMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as RemapPropertiesToUnknown<PreviewIFrameComponentSelectedMessage>).type ===
    previewIFrameComponentSelectedMessageType;

export const isPreviewIFrameScrollMessage = (data: unknown): data is PreviewIFrameScrollMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as RemapPropertiesToUnknown<PreviewIFrameScrollMessage>).type ===
    previewIFrameScrollMessageType;

// Component hovered in iframe (iframe -> parent)
export const previewIFrameComponentHoveredMessageType = 'preview-component-hovered';
export type PreviewIFrameComponentHoveredMessage = {
  type: typeof previewIFrameComponentHoveredMessageType;
  componentId: string;
  rect: { left: number; top: number; width: number; height: number };
};

export const isPreviewIFrameComponentHoveredMessage = (
  data: unknown,
): data is PreviewIFrameComponentHoveredMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as RemapPropertiesToUnknown<PreviewIFrameComponentHoveredMessage>).type ===
    previewIFrameComponentHoveredMessageType;

// Set selected component ID (parent -> iframe)
export const previewIFrameSetSelectedComponentMessageType = 'parent-set-selected-component';
export type PreviewIFrameSetSelectedComponentMessage = {
  type: typeof previewIFrameSetSelectedComponentMessageType;
  componentId: string | null;
};

export const isPreviewIFrameSetSelectedComponentMessage = (
  data: unknown,
): data is PreviewIFrameSetSelectedComponentMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as RemapPropertiesToUnknown<PreviewIFrameSetSelectedComponentMessage>).type ===
    previewIFrameSetSelectedComponentMessageType;

// Get component rect request (parent -> iframe)
export const previewIFrameGetComponentRectMessageType = 'parent-get-component-rect';
export type PreviewIFrameGetComponentRectMessage = {
  type: typeof previewIFrameGetComponentRectMessageType;
  requestId: string;
  componentId: string;
};

export const isPreviewIFrameGetComponentRectMessage = (
  data: unknown,
): data is PreviewIFrameGetComponentRectMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as RemapPropertiesToUnknown<PreviewIFrameGetComponentRectMessage>).type ===
    previewIFrameGetComponentRectMessageType;

// Component rect response (iframe -> parent)
export const previewIFrameComponentRectMessageType = 'preview-component-rect';
export type PreviewIFrameComponentRectMessage = {
  type: typeof previewIFrameComponentRectMessageType;
  requestId: string;
  componentId: string;
  rect: { left: number; top: number; width: number; height: number } | null;
};

export const isPreviewIFrameComponentRectMessage = (
  data: unknown,
): data is PreviewIFrameComponentRectMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as RemapPropertiesToUnknown<PreviewIFrameComponentRectMessage>).type ===
    previewIFrameComponentRectMessageType;

export type DesignerPreviewObject =
  | PreviewComponentObject
  | PreviewComponentPropsFormObject
  | PreviewCustomFieldsFormObject
  | PreviewFormObject
  | PreviewFormConfirmationMessageObject
  | PreviewFormStepObject
  | PreviewFormTemplateObject
  | PreviewFormTemplateConfirmationMessageObject
  | PreviewFormTemplateStepObject
  | PreviewLayoutObject
  | PreviewLayoutTemplateObject
  | PreviewLessonObject
  | PreviewPageObject
  | PreviewPageTemplateObject
  | PreviewPageTemplatePropsFormObject
  | PreviewRemoteComponentPropsFormObject
  | PreviewStageObject;

export type DesignerPreviewObjectWithComponents = DesignerPreviewObject & {
  components: DesignerComponent[];
  platformId?: string;
};

// Object types.

export type PreviewComponentObject = {
  type: typeof DesignerObjectType.Component;
  customProps: ContextData | null;
};

export type PreviewComponentPropsFormObject = {
  type: typeof DesignerObjectType.ComponentPropsForm;
};

export type PreviewRemoteComponentPropsFormObject = {
  type: typeof DesignerObjectType.RemoteComponentPropsForm;
};

export type PreviewCustomFieldsFormObject = {
  type: typeof DesignerObjectType.CustomFieldsForm;
};

export type PreviewFormObject = {
  type: typeof DesignerObjectType.Form;
  id: string;
  confirmationAction: ConfirmationAction;
  confirmationMessageComponents: DesignerComponent[] | null;
  confirmationRedirectUrl: string | null;
  confirmationRedirectUrlParameters: Array<{
    id: string;
    parameter: string;
    componentName: string;
  }>;
  steps: Array<{ id: string; components: DesignerComponent[] }>;
};

export type PreviewFormConfirmationMessageObject = {
  type: typeof DesignerObjectType.FormConfirmationMessage;
};

export type PreviewFormStepObject = {
  type: typeof DesignerObjectType.FormStep;
};

export type PreviewFormTemplateObject = {
  type: typeof DesignerObjectType.FormTemplate;
  id: string;
  confirmationAction: ConfirmationAction;
  confirmationMessageComponents: DesignerComponent[] | null;
  confirmationRedirectUrl: string | null;
  confirmationRedirectUrlParameters: Array<{
    id: string;
    parameter: string;
    componentName: string;
  }>;
  steps: Array<{ id: string; components: DesignerComponent[] }>;
};

export type PreviewFormTemplateConfirmationMessageObject = {
  type: typeof DesignerObjectType.FormTemplateConfirmationMessage;
};

export type PreviewFormTemplateStepObject = {
  type: typeof DesignerObjectType.FormTemplateStep;
};

export type PreviewLayoutObject = {
  type: typeof DesignerObjectType.Layout;
  // TODO: bodyStyles
};

export type PreviewLayoutTemplateObject = {
  type: typeof DesignerObjectType.LayoutTemplate;
  // TODO: bodyStyles
};

export type PreviewLessonObject = {
  type: typeof DesignerObjectType.Lesson;
  id: string;
  lessonCompletionMode: LessonCompletionMode | null;
  course: {
    id: string;
    lessonCompletionMode: LessonCompletionMode;
  };
};

export type PreviewPageObject = {
  type: typeof DesignerObjectType.Page;
  title: string;
  path: string;
  metaTitle: string | null;
  metaDescription: string | null;
  metaRobots: string[] | null;
  featuredImageUrl: string | null;
  publishedAt: Date | null;
  bodyStyles: ComponentStyleRules | null;
  props: ContextData | null;
  resolvedLayout: DesignerComponent[] | null;
};

export type PreviewPageTemplateObject = {
  type: typeof DesignerObjectType.PageTemplate;
  siteId: string | null;
  title: string;
  metaTitle: string | null;
  metaDescription: string | null;
  metaRobots: string[] | null;
  bodyStyles: ComponentStyleRules | null;
  props: ContextData | null;
};

export type PreviewPageTemplatePropsFormObject = {
  type: typeof DesignerObjectType.PageTemplatePropsForm;
};

export type PreviewStageObject = {
  type: typeof DesignerObjectType.Stage;
  sequenceId: string;
};
